import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm } from './supabaseService';

const styles = {
  container: {
    maxWidth: 900,
    margin: '40px auto',
    padding: 0,
    background: '#f6f7fb',
    borderRadius: 0,
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'auto',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 0 80px 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  card: {
    background: 'linear-gradient(135deg, #fff 80%, #ffe5e5 100%)',
    borderRadius: 18,
    boxShadow: '0 6px 32px rgba(255,0,0,0.08), 0 1.5px 6px rgba(0,0,0,0.04)',
    padding: '28px 18px',
    width: '100%',
    maxWidth: 430,
    margin: '0 auto 28px auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    border: '1.5px solid #f2dede',
  },
  header: {
    textAlign: 'center',
    marginBottom: 0,
  },
  logo: {
    width: 80,
    height: 'auto',
    marginBottom: 8,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(255,0,0,0.08)',
  },
  formTitle: {
    fontWeight: 900,
    fontSize: '2.2rem',
    color: '#c00',
    margin: '0 0 18px 0',
    letterSpacing: 0.5,
    lineHeight: 1.1,
  },
  section: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    padding: '18px 12px',
    marginBottom: 18,
    border: '1.5px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    marginBottom: 8,
    color: '#b00',
    letterSpacing: 0.5,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 14,
    width: '100%',
  },
  label: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: 6,
    color: '#222',
    letterSpacing: 0.1,
  },
  input: {
    width: '100%',
    padding: '16px 12px',
    fontSize: 18,
    border: '1.5px solid #ccc',
    borderRadius: 10,
    background: '#f9f9f9',
    marginBottom: 0,
    boxSizing: 'border-box',
    transition: 'border 0.2s',
    outline: 'none',
  },
  checkbox: {
    width: 24,
    height: 24,
    marginLeft: 8,
    marginRight: 8,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
    flexWrap: 'wrap',
  },
  button: {
    width: '100%',
    padding: '18px 0',
    background: 'linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 18,
    cursor: 'pointer',
    marginTop: 24,
    boxShadow: '0 4px 16px rgba(255,0,0,0.2)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  stickyFooter: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100%',
    background: 'linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%)',
    padding: '12px 18px',
    boxShadow: '0 -2px 8px rgba(255,0,0,0.08)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
  },
};

const hazardRows = [
  {
    hazard: 'Slips, trips and falls',
    harmed: 'Staff, customers and visitors may be injured if they trip over objects or slip on spillages.',
    doing: 'General good housekeeping is carried out. All areas well lit, including stairs. No trailing leads or cables. Keep work areas clear, eg no boxes left in walkways, deliveries stored immediately.',
    control: 'Maintain good housekeeping practice. Ensure any spillages are immediately cleaned up.',
    who: 'All staff, supervisor to monitor',
    when: 'From now on',
  },
  {
    hazard: 'Manual handling',
    harmed: 'Colleagues performing the task. Awkward postures, repetitive movements of arms, legs and back or previous/existing injury can increase the risk.',
    doing: 'Training colleagues on how to operate and use the tools. Visual checks of equipment before each use.',
    control: 'Maintain good storage and transportation of equipment, to reduce any damage to equipment.',
    who: 'All staff, supervisor to monitor',
    when: 'From now on',
  },
  {
    hazard: 'Power Tools, Hoovers, Floor scrubbers',
    harmed: 'Staff, customers and visitors may be injured if they trip over objects or slip on spillages.',
    doing: 'Training colleagues on how to operate and use the tools. Visual checks of equipment before each use.',
    control: 'Maintain good storage and transportation of equipment, to reduce any damage to equipment.',
    who: 'All staff, supervisor to monitor',
    when: 'From now on',
  },
  {
    hazard: 'Cleaning Chemicals',
    harmed: 'Colleagues who are using the cleaning products. Customers who maybe interacting with the area.',
    doing: 'Colleagues are aware of the cleaning products they are using and what procedure to follow.',
    control: 'Maintain good housekeeping practice. Utilize the ERIC safe working practice. Eliminate. Reduce. Isolate. Control.',
    who: 'All staff, supervisor to monitor',
    when: 'From now on',
  },
];

export default function Form1Screen() {
  const [formValues, setFormValues] = useState({
    assessorName: '',
    assessmentDate: '',
    department: '',
    contactInfo: '',
    lawPosterLocation: '',
    firstAidBoxLocation: '',
    accidentBookLocation: '',
    responsibility: '',
    reviewDate: '',
    trainingRequirements: '',
    fireSafetyMeasures: '',
    emergencyProcedures: '',
    additionalInfo: '',
    companyName: '',
    riskAssessmentDate: '',
    comments: '',
    done: [false, false, false, false],
  });

  const pdfRef = useRef();

  const handleInputChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (idx) => {
    setFormValues((prev) => ({
      ...prev,
      done: prev.done.map((v, i) => (i === idx ? !v : v)),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    generatePDF();
  };

  const generatePDF = async () => {
    try {
      const element = pdfRef.current;
      
      // Generate PDF as blob
      const pdfBlob = await html2pdf()
        .set({
          margin: 0.5,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        })
        .from(element)
        .outputPdf('blob');

      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Risk_Assessment_Form_${currentDate}.pdf`;

      // Save to Firebase
      const formData = {
        formType: 'form1',
        title: 'Risk Assessment Form',
        location: formValues.department || 'Not specified',
        createdBy: formValues.assessorName || 'Not specified',
        assessmentDate: formValues.assessmentDate || currentDate,
        companyName: formValues.companyName || 'Married to the Mop N/W Ltd.',
        contactInfo: formValues.contactInfo || 'Not specified',
        lawPosterLocation: formValues.lawPosterLocation || 'Not specified',
        firstAidBoxLocation: formValues.firstAidBoxLocation || 'Not specified',
        accidentBookLocation: formValues.accidentBookLocation || 'Not specified',
        responsibility: formValues.responsibility || 'Not specified',
        reviewDate: formValues.reviewDate || 'Not specified',
        trainingRequirements: formValues.trainingRequirements || 'Not specified',
        fireSafetyMeasures: formValues.fireSafetyMeasures || 'Not specified',
        emergencyProcedures: formValues.emergencyProcedures || 'Not specified',
        additionalInfo: formValues.additionalInfo || 'Not specified',
        comments: formValues.comments || 'Not specified'
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div style={styles.container} className="form1-container">
      <style>{`
        @media (max-width: 600px) {
          .form1-container {
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: #f6f7fb !important;
          }
          .form1-scroll {
            padding: 12px 0 90px 0 !important;
          }
          .form1-card {
            padding: 18px 6px !important;
            border-radius: 12px !important;
            margin-bottom: 18px !important;
            max-width: 100vw !important;
          }
          .form1-logo {
            width: 60px !important;
            margin-bottom: 4px !important;
          }
          .form1-title {
            font-size: 1.3rem !important;
          }
          .form1-formTitle {
            font-size: 1.5rem !important;
          }
          .form1-sectionTitle {
            font-size: 1.05rem !important;
          }
          .form1-label {
            font-size: 13px !important;
            margin-bottom: 3px !important;
          }
          .form1-button {
            font-size: 16px !important;
            padding: 14px 0 !important;
            border-radius: 10px !important;
            max-width: 100vw !important;
          }
          .form1-input {
            font-size: 16px !important;
            padding: 12px !important;
            border-radius: 8px !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100vw !important;
          }
          .form1-stickyFooter {
            position: fixed !important;
            left: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            border-radius: 0 !important;
            z-index: 1000 !important;
            box-shadow: 0 -2px 8px rgba(255,0,0,0.08) !important;
            background: linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%) !important;
            padding: 0 !important;
            margin: 0 !important;
            display: flex !important;
            justify-content: center !important;
          }
        }
        @media (max-width: 400px) {
          .form1-title {
            font-size: 1.1rem !important;
          }
          .form1-formTitle {
            font-size: 1.1rem !important;
          }
          .form1-sectionTitle {
            font-size: 0.95rem !important;
          }
          .form1-label {
            font-size: 11px !important;
          }
        }
      `}</style>
      <div style={styles.scrollArea} className="form1-scroll">
        <div style={styles.card} className="form1-card">
          <div style={styles.header}>
            <img src="/logo.jpg" alt="Logo" style={styles.logo} className="form1-logo" />
            <div style={styles.formTitle} className="form1-formTitle">Risk Assessment Form</div>
          </div>
          
          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form1-sectionTitle">Assessment Details</div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Assessor Name:</label>
              <input style={styles.input} className="form1-input" name="assessorName" value={formValues.assessorName} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Assessment Date:</label>
              <input style={styles.input} className="form1-input" name="assessmentDate" value={formValues.assessmentDate} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Department/Location:</label>
              <input style={styles.input} className="form1-input" name="department" value={formValues.department} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Contact Information:</label>
              <input style={styles.input} className="form1-input" name="contactInfo" value={formValues.contactInfo} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form1-sectionTitle">Policy Information</div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Health and Safety Law Poster Location:</label>
              <input style={styles.input} className="form1-input" name="lawPosterLocation" value={formValues.lawPosterLocation} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">First-aid Box Location:</label>
              <input style={styles.input} className="form1-input" name="firstAidBoxLocation" value={formValues.firstAidBoxLocation} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Accident Book Location:</label>
              <input style={styles.input} className="form1-input" name="accidentBookLocation" value={formValues.accidentBookLocation} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form1-sectionTitle">Additional Details</div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Responsibility (Name/Position):</label>
              <input style={styles.input} className="form1-input" name="responsibility" value={formValues.responsibility} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Risk Assessment Review Date:</label>
              <input style={styles.input} className="form1-input" name="reviewDate" value={formValues.reviewDate} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Training Requirements:</label>
              <input style={styles.input} className="form1-input" name="trainingRequirements" value={formValues.trainingRequirements} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Fire Safety Measures:</label>
              <input style={styles.input} className="form1-input" name="fireSafetyMeasures" value={formValues.fireSafetyMeasures} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Emergency Procedures:</label>
              <input style={styles.input} className="form1-input" name="emergencyProcedures" value={formValues.emergencyProcedures} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Additional Information:</label>
              <input style={styles.input} className="form1-input" name="additionalInfo" value={formValues.additionalInfo} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Company Name:</label>
              <input style={styles.input} className="form1-input" name="companyName" value={formValues.companyName} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Date of risk assessment:</label>
              <input style={styles.input} className="form1-input" name="riskAssessmentDate" value={formValues.riskAssessmentDate} onChange={handleInputChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} className="form1-label">Comments:</label>
              <input style={styles.input} className="form1-input" name="comments" value={formValues.comments} onChange={handleInputChange} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle} className="form1-sectionTitle">Hazards Table (Tick Done):</div>
            {hazardRows.map((row, idx) => (
              <div key={idx} style={styles.checkboxRow}>
                <span style={{ flex: 2, fontSize: 14 }}>{row.hazard}</span>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={formValues.done[idx]}
                  onChange={() => handleCheckboxChange(idx)}
                />
                <span style={{ fontSize: 14 }}>Done</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hidden PDF template */}
        <div style={{ display: 'none' }}>
          <div ref={pdfRef}>
            <html>
              <head>
                <style>
                  {`
                  body { font-family: Arial, sans-serif; }
                  table { width: 100%; border-collapse: collapse; font-size: 11px; }
                  th, td { border: 1px solid black; padding: 5px; text-align: left; }
                  .header { background-color: #f2f2f2; }
                  .title { text-align: center; margin-top: 20px; margin-bottom: 20px; }
                  .checkbox-cell { text-align: center; }
                  .no-break { page-break-inside: avoid; }
                  `}
                </style>
              </head>
              <body>
                <h1 className="title">Risk Assessment</h1>
                <h2>Health and Safety Policy</h2>
                <div style={{ textAlign: 'right', marginTop: 20 }}>
                  <img src="/logo.jpg" alt="Logo" style={{ maxWidth: '100%', width: '300px', height: 'auto' }} />
                </div>
                <table>
                  <tbody>
                    <tr>
                      <th>Assessor Name</th>
                      <td>{formValues.assessorName}</td>
                    </tr>
                    <tr>
                      <th>Assessment Date</th>
                      <td>{formValues.assessmentDate}</td>
                    </tr>
                    <tr>
                      <th>Department/Location</th>
                      <td>{formValues.department}</td>
                    </tr>
                    <tr>
                      <th>Contact Information</th>
                      <td>{formValues.contactInfo}</td>
                    </tr>
                  </tbody>
                </table>
                <table>
                  <tbody>
                    <tr>
                      <th colSpan="2">This is the statement of general policy and arrangements for:</th>
                      <td></td>
                    </tr>
                    <tr>
                      <th>Signed:</th>
                      <td>(Employer)</td>
                      <td></td>
                    </tr>
                    <tr>
                      <th>Health and safety law poster is displayed at (location)</th>
                      <td>{formValues.lawPosterLocation}</td>
                    </tr>
                    <tr>
                      <th>First-aid box is located:</th>
                      <td>{formValues.firstAidBoxLocation}</td>
                    </tr>
                    <tr>
                      <th>Accident book is located:</th>
                      <td>{formValues.accidentBookLocation}</td>
                    </tr>
                    <tr>
                      <th>Responsibility (Name/Position)</th>
                      <td>{formValues.responsibility}</td>
                    </tr>
                    <tr>
                      <th>Risk Assessment Review Date</th>
                      <td>{formValues.reviewDate}</td>
                    </tr>
                    <tr>
                      <th>Training Requirements</th>
                      <td>{formValues.trainingRequirements}</td>
                    </tr>
                    <tr>
                      <th>Fire Safety Measures</th>
                      <td>{formValues.fireSafetyMeasures}</td>
                    </tr>
                    <tr>
                      <th>Emergency Procedures</th>
                      <td>{formValues.emergencyProcedures}</td>
                    </tr>
                    <tr>
                      <th>Additional Information</th>
                      <td>{formValues.additionalInfo}</td>
                    </tr>
                  </tbody>
                </table>
                <h2>Company name: {formValues.companyName}</h2>
                <h2>Date of risk assessment: {formValues.riskAssessmentDate}</h2>
                {/* Force hazards table to new page */}
                <div style={{ pageBreakBefore: 'always' }}>
                  <table className="no-break">
                    <thead>
                      <tr className="header">
                        <th>What are the hazards?</th>
                        <th>Who might be harmed and how?</th>
                        <th>What are you already doing?</th>
                        <th>Do you need to do anything else to control this risk?</th>
                        <th>Action by who?</th>
                        <th>Action by when?</th>
                        <th>Done</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hazardRows.map((row, idx) => (
                        <tr key={idx} className="no-break">
                          <td>{row.hazard}</td>
                          <td>{row.harmed}</td>
                          <td>{row.doing}</td>
                          <td>{row.control}</td>
                          <td>{row.who}</td>
                          <td>{row.when}</td>
                          <td className="checkbox-cell">{formValues.done[idx] ? '☑' : '☐'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h2>Comments:</h2>
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <img src="/logo.jpg" alt="Logo" style={{ maxWidth: '100%', width: '300px', height: 'auto' }} />
                </div>
                <p>{formValues.comments}</p>
              </body>
            </html>
          </div>
        </div>

        <div style={styles.stickyFooter} className="form1-stickyFooter">
          <button style={styles.button} className="form1-button" onClick={generatePDF}>Download PDF</button>
        </div>
      </div>
    </div>
  );
}
